import { createSteeringURL } from '@/api'
import { useI18n } from '@/i18n'
import { steeringAuthInit, SteeringUser, useAuthStore, useHasUserPermission } from '@/stores/auth'
import { useRRuleLabel } from '@/stores/rrules'
import { useShowStore } from '@/stores/shows'
import {
  RRule,
  Schedule,
  ScheduleConflictSolution,
  ScheduleCreateData,
  SchedulesProlongation,
  ScheduleUpdateData,
} from '@/types'
import { useFormattedTime, useTimeToDate } from '@/util'
import { aggregateWithIdsParameter, useStoreHelpers } from '@/util/api'
import {
  APICreate,
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  BaseRequestOptions,
  createExtendableAPI,
  ExtendableAPI,
} from '@rokoli/bnb/drf'
import { addDays } from 'date-fns'
import { defineStore } from 'pinia'
import { computed, MaybeRefOrGetter, toValue } from 'vue'

type SchedulePartialUpdateData = Partial<
  Pick<ScheduleCreateData['schedule'], 'lastDate' | 'defaultMediaId' | 'repetitionOfId'>
>

export type ScheduleConflictResolver<T> = (
  schedule: ScheduleCreateData['schedule'],
  solutions: ScheduleCreateData['solutions'],
) => Promise<T>

export function APIProlong(api: ExtendableAPI<Schedule>) {
  async function prolongProgram(
    until: string,
    options: BaseRequestOptions | undefined = undefined,
  ) {
    const query = options?.query ?? new URLSearchParams()
    const res = await fetch(
      api.createRequest(api.endpoint('prolong', query), options?.requestInit, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ until }),
      }),
    )
    await api.maybeThrowResponse(res)
  }

  async function getProlongationState(
    options: BaseRequestOptions | undefined = undefined,
  ): Promise<SchedulesProlongation> {
    const query = options?.query ?? new URLSearchParams()
    const res = await fetch(api.createRequest(api.endpoint('prolong', query), options?.requestInit))
    await api.maybeThrowResponse(res)
    return res.json()
  }

  return { prolongProgram, getProlongationState }
}

export const useScheduleStore = defineStore('schedules', () => {
  const endpoint = createSteeringURL.prefix('schedules')
  const { api, base } = createExtendableAPI<Schedule>(endpoint, steeringAuthInit)
  const listOperations = APIListPaginated(api)
  const retrieveOperations = APIRetrieve(api, {
    aggregate: aggregateWithIdsParameter(listOperations.listIsolated),
  })

  return {
    ...base,
    ...listOperations,
    ...retrieveOperations,
    ...APICreate<Schedule, ScheduleCreateData>(api),
    ...APIUpdate<Schedule, ScheduleUpdateData, SchedulePartialUpdateData>(api),
    ...APIRemove(api),
    ...APIProlong(api),
    ...useStoreHelpers({ ...base, ...listOperations, ...retrieveOperations }),
  }
})

export function useConflictSolutions(translate: (v: string) => string) {
  const t = translate
  const modes: readonly ScheduleConflictSolution[] = Object.freeze([
    'ours',
    'theirs',
    'theirs-start',
    'theirs-end',
    'theirs-both',
    'ours-start',
    'ours-end',
    'ours-both',
  ])
  const modeChoices = computed<
    { mode: ScheduleConflictSolution; label: string; description: string }[]
  >(() =>
    modes.map((mode) => ({
      mode,
      label: t(`conflictResolution.strategies.${mode}.label`),
      description: t(`conflictResolution.strategies.${mode}.description`),
    })),
  )

  return { modes, modeChoices }
}

export type RepetitionType = 'next-day' | 'next-business-day' | 'n-days-later'

export function useRepetitionRules(translate: (v: string) => string) {
  const t = translate
  return computed<
    { id: RepetitionType; label: string; defaults: Partial<ScheduleCreateData['schedule']> }[]
  >(() => [
    {
      id: 'next-day',
      label: t('repetition.nextDay'),
      defaults: {
        addBusinessDaysOnly: false,
        addDaysNo: 1,
      },
    },
    {
      id: 'next-business-day',
      label: t('repetition.nextBusinessDay'),
      defaults: {
        addBusinessDaysOnly: true,
        addDaysNo: 1,
      },
    },
    {
      id: 'n-days-later',
      label: t('repetition.nDaysLater'),
      defaults: {},
    },
  ])
}

export function useStartEnd(
  firstDate: MaybeRefOrGetter<string | Date | null | undefined>,
  startTime: MaybeRefOrGetter<string | null | undefined>,
  endTime: MaybeRefOrGetter<string | number | null | undefined>,
) {
  const startTimeDate = useTimeToDate(startTime, { referenceDate: firstDate })
  const referenceEndDate = computed(() => {
    const _firstDate = toValue(firstDate)
    const _endTime = toValue(endTime)
    const _startTime = toValue(startTime)
    // if endTime is a number, useTimeToDate should calculate based on the start time
    if (typeof _endTime === 'number') return startTimeDate.value
    if (!_endTime || !_startTime || !_firstDate) return _firstDate
    return _endTime < _startTime ? addDays(_firstDate, 1) : _firstDate
  })

  return {
    startTime: startTimeDate,
    endTime: useTimeToDate(endTime, { referenceDate: referenceEndDate }),
  }
}

export function useScheduleTimes(
  schedule: MaybeRefOrGetter<
    Pick<Schedule, 'firstDate' | 'startTime' | 'endTime'> | null | undefined
  >,
) {
  return useStartEnd(
    () => toValue(schedule)?.firstDate ?? new Date(),
    () => toValue(schedule)?.startTime,
    () => toValue(schedule)?.endTime,
  )
}

export function useScheduleLabel(
  schedule: MaybeRefOrGetter<
    Pick<Schedule, 'firstDate' | 'startTime' | 'endTime' | 'byWeekday'> | null | undefined
  >,
  rrule: MaybeRefOrGetter<RRule | null | undefined>,
) {
  const { t } = useI18n()
  const { label } = useRRuleLabel(rrule, schedule)

  const { startTime, endTime } = useScheduleTimes(schedule)
  const startTimeStr = useFormattedTime(startTime)
  const endTimeStr = useFormattedTime(endTime)

  return computed(() => {
    const _schedule = toValue(schedule)
    const rrule = toValue(label)
    if (!_schedule || !rrule) return ''
    const start = startTimeStr.value as string
    if (!_schedule.endTime) return t('schedule.labels.description.noEndTime', { rrule, start })
    const end = endTimeStr.value as string
    return t('schedule.labels.description.default', { rrule, start, end })
  })
}

export const createScheduleWithSolutions: ScheduleConflictResolver<Schedule> = async (
  schedule,
  solutions,
) => {
  const scheduleStore = useScheduleStore()
  return scheduleStore.create({ schedule, solutions })
}

export function useIsScheduleOwner(id: MaybeRefOrGetter<Schedule['id'] | null>) {
  const authStore = useAuthStore()
  const showStore = useShowStore()
  const scheduleStore = useScheduleStore()
  const { obj: schedule } = scheduleStore.use(id)
  const { obj: show } = showStore.use(() => schedule?.value?.showId)
  const canEditAll = useHasUserPermission(['program.update_schedule'])

  return computed(
    () =>
      canEditAll.value ||
      (show.value?.ownerIds?.includes?.(authStore?.steeringUser?.id as SteeringUser['id']) ??
        false),
  )
}
